/**
 * ContactDetailModal — View and edit contact details with notes (Wave 12+).
 *
 * Displays:
 * - Tab bar: Info | Status | Notes | Meetings | News | Sources
 * - Contact info (name, title, email, phone)
 * - Relationship status and influence level
 * - Notes and talking points
 * - Add new note form
 * - Meeting recordings linked to this contact (Phase 2)
 * - News stories tagged to this contact (the department feed's contact
 *   tags, surfaced on the person — a story about Mike Marshall is on Mike
 *   Marshall's profile, not just the agency page)
 * - Sources: how this profile got filled in. A contact accretes facts from
 *   several directions — LinkedIn discovery, the Gmail harvest matching in
 *   through services/crm/identity.js, meetings, Ryan typing — and this tab
 *   is where you see which was which: the claim on every field (decision
 *   #22 provenance, the same vocabulary the org-chart pips use) over the
 *   logged change trail and the tenure history.
 */

import React, { useState } from 'react';
import useModalBehavior from '../../hooks/useModalBehavior';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, User, Mail, Phone, Briefcase, MessageSquare, Plus,
  Loader2, Pencil, Trash2, StickyNote, Target, AlertCircle,
  Building2, AlertTriangle, Volume2, Linkedin, Newspaper, ExternalLink,
  Bot, BadgeCheck, Footprints, History,
} from 'lucide-react';
// The provenance vocabulary is the org chart's — one wording for "where did
// this come from", whether you're looking at a card's pip or this profile.
import { describeClaim, claimKind, fieldLabel, PIP_LABEL, PIP_BLURB } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import DepartmentSearchDropdown from './DepartmentSearchDropdown';
import DuplicateContactModal from './DuplicateContactModal';
import PlaudRecordingCard from './PlaudRecordingCard';
import PlaudTranscriptModal from './PlaudTranscriptModal';
import NewsItemRow from '../NewsFeed/NewsItemRow';
import '../NewsFeed/NewsFeed.css';
import './ContactDetailModal.css';
import OverlayPortal from '../shared/OverlayPortal';

const TABS = [
  { id: 'info', label: 'Info' },
  { id: 'status', label: 'Status' },
  { id: 'notes', label: 'Notes' },
  { id: 'meetings', label: 'Meetings', icon: Volume2 },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'sources', label: 'Sources', icon: History },
];

/** The fields that carry a claim, in the order the Sources tab lists them. */
const PROV_FIELDS = ['email', 'phone', 'linkedin_url', 'title', 'org_unit', 'reports_to'];

const CLAIM_ICON = { guessed: AlertCircle, automated: Bot, verified: BadgeCheck, field: Footprints };

/**
 * One field's claim, rendered inline next to the value it explains: who put
 * it there and how sure they were. Nothing renders for a value that predates
 * provenance — a blank badge would imply an absence of trust rather than an
 * absence of a record.
 */
function ClaimBadge({ entry }) {
  const kind = claimKind(entry);
  if (!kind) return null;
  const Icon = CLAIM_ICON[kind] || Bot;
  return (
    <span
      className={`cdm-claim cdm-claim-${kind}`}
      title={`${PIP_LABEL[kind]} — ${PIP_BLURB[kind]}\n${describeClaim(entry)}`}
    >
      <Icon size={11} aria-hidden="true" />
      {PIP_LABEL[kind]}
    </span>
  );
}

const REL_OPTIONS = [
  { value: 'champion', label: 'Champion', color: 'var(--color-success)' },
  { value: 'strong', label: 'Strong', color: 'var(--color-success)' },
  { value: 'developing', label: 'Developing', color: 'var(--color-primary)' },
  { value: 'new', label: 'New', color: 'var(--color-warning)' },
  { value: 'blocker', label: 'Blocker', color: 'var(--color-error)' },
  { value: 'dormant', label: 'Dormant', color: 'var(--color-text-muted)' },
];

const INFLUENCE_OPTIONS = [
  { value: 'decision_maker', label: 'Decision Maker' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'unknown', label: 'Unknown' },
];

const NOTE_TYPES = [
  { value: 'note', label: 'Note', icon: StickyNote },
  { value: 'talking_point', label: 'Talking Point', icon: Target },
];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ContactDetailModal({ contact, onClose, onUpdate, allContacts = [] }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    relationship_status: contact.relationship_status || '',
    influence_level: contact.influence_level || '',
    title: contact.title || '',
    reports_to_id: contact.reports_to_id || '',
    department_id: contact.department_id || '',
    first_name: contact.first_name || '',
    last_name: contact.last_name || '',
    email: contact.email || '',
    phone: contact.phone || '',
    linkedin_url: contact.linkedin_url || '',
  });
  const [isEditingInfo, setIsEditingInfo] = useState(false);

  // Local display state - updated immediately on save for responsive UI
  const [displayInfo, setDisplayInfo] = useState({
    first_name: contact.first_name || '',
    last_name: contact.last_name || '',
    title: contact.title || '',
    email: contact.email || '',
    phone: contact.phone || '',
    linkedin_url: contact.linkedin_url || '',
  });

  // Duplicate detection state
  const [duplicateData, setDuplicateData] = useState(null);
  // Human-readable failure for any contact mutation — a failed save/delete/
  // merge must never be silent. { action, detail } or null.
  const [mutationError, setMutationError] = useState(null);
  const failWith = (action) => (err) => setMutationError({
    action,
    detail: err?.message || 'Unknown error',
  });

  // Esc / backdrop-guard / focus behavior; open edit forms count as dirty so
  // a stray scrim click asks before discarding (UX-AUDIT X-3).
  const modal = useModalBehavior(onClose, {
    isDirty: () => isEditing || isEditingInfo,
  });
  const [isMerging, setIsMerging] = useState(false);

  // Transcript modal state
  const [viewingTranscript, setViewingTranscript] = useState(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Track if department is changing (to show warning about reports_to being cleared)
  const isDepartmentChanging = editForm.department_id && editForm.department_id !== contact.department_id;

  // Fetch departments for the organization dropdown
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.get('/api/departments'),
    staleTime: 5 * 60_000, // 5 minutes
  });

  // Build manager options (exclude self)
  const managerOptions = allContacts
    .filter((c) => c.id !== contact.id)
    .map((c) => ({
      value: c.id,
      label: `${c.first_name} ${c.last_name || ''}`.trim() + (c.title ? ` — ${c.title}` : ''),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('note');
  const [armedNoteId, setArmedNoteId] = useState(null); // X-7: two-click delete arm

  const fullName = `${displayInfo.first_name} ${displayInfo.last_name || ''}`.trim();

  // Fetch notes for this contact
  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['contact-notes', contact.id],
    queryFn: () => apiClient.get(`/api/workspace/contacts/${contact.id}/notes`),
    staleTime: 30_000,
  });

  const notes = notesData?.notes || [];

  // How this profile got filled in (decision #22's trail). Fetched only when
  // the Sources tab is open — it is a per-contact join nobody needs on every
  // card click.
  const { data: historyData, isLoading: historyLoading, error: historyError } = useQuery({
    queryKey: ['contact-history', contact.id],
    queryFn: () => apiClient.get(`/api/workspace/contacts/${contact.id}/history`),
    staleTime: 30_000,
    enabled: activeTab === 'sources',
  });
  const provenance = contact.field_provenance || {};
  const changes = historyData?.changes || [];

  // Employment history reads from its own endpoint rather than the history
  // payload's tenures: it carries the employer for orgs that are not one of
  // our accounts, plus each row's source. Same rows, fuller shape.
  const { data: employmentData } = useQuery({
    queryKey: ['contact-employment', contact.id],
    queryFn: () => apiClient.get(`/api/workspace/contacts/${contact.id}/employment`),
    staleTime: 30_000,
    enabled: activeTab === 'sources',
  });
  const tenures = employmentData?.tenures || historyData?.tenures || [];

  // Paste an Experience block → the server parses it and says what it read
  // (one parser, so this preview and the write cannot disagree) → you save.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState(null); // { plan, refused, unparsed } | null

  const employmentMutation = useMutation({
    mutationFn: ({ text, dryRun }) => apiClient.post(
      `/api/workspace/contacts/${contact.id}/employment`,
      { text, dry_run: dryRun, source: 'paste', basis: 'Pasted from a profile' }
    ),
    onSuccess: (data, vars) => {
      if (vars.dryRun) { setPreview(data); return; }
      setPreview(null); setPasteText(''); setPasteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['contact-employment', contact.id] });
      queryClient.invalidateQueries({ queryKey: ['contact-history', contact.id] });
    },
    onError: failWith('save employment history'),
  });

  // Fetch recordings linked to this contact
  const { data: recordingsData, isLoading: recordingsLoading } = useQuery({
    queryKey: ['contact-recordings', contact.id],
    queryFn: () => apiClient.get(`/api/plaud/contact/${contact.id}`),
    staleTime: 60_000,
    enabled: activeTab === 'meetings',
  });

  const recordings = recordingsData?.recordings || [];

  // News tagged to this person. Fetched on open (not on tab click) so the
  // tab count is on screen the moment the profile is — "has Mike been in
  // the news?" is the question this answers, and a count you have to click
  // for isn't an answer.
  const { data: newsData, isLoading: newsLoading, error: newsError } = useQuery({
    queryKey: ['department-news', 'contact', contact.id],
    queryFn: () => apiClient.get(`/api/v1/department-news/contacts/${contact.id}`, { query: { limit: 25 } }),
    enabled: !!contact.id,
    staleTime: 60_000,
  });
  const newsItems = newsData?.items || [];
  const newsTotal = newsData?.counts?.total ?? newsItems.length;

  // Update contact mutation with duplicate detection handling
  const updateMutation = useMutation({
    mutationFn: async (updates) => {
      try {
        return await apiClient.put(`/api/workspace/contacts/${contact.id}`, updates);
      } catch (err) {
        // The backend returns 409 { error: 'potential_duplicates', duplicates }.
        // apiClient already surfaces the parsed body as err.data (CRM-12) — the
        // old code re-issued the PUT via raw fetch just to read it, a second
        // write that also risked double-applying the update.
        if (err.status === 409 && err.data?.error === 'potential_duplicates') {
          throw { isDuplicate: true, duplicates: err.data.duplicates || [] };
        }
        throw err;
      }
    },
    onMutate: (variables) => {
      // Optimistic: patch display info immediately; snapshot for rollback.
      const previousDisplay = displayInfo;
      setDisplayInfo((prev) => ({
        ...prev,
        ...(variables.first_name !== undefined && { first_name: variables.first_name }),
        ...(variables.last_name !== undefined && { last_name: variables.last_name }),
        ...(variables.title !== undefined && { title: variables.title }),
        ...(variables.email !== undefined && { email: variables.email }),
        ...(variables.phone !== undefined && { phone: variables.phone }),
        ...(variables.linkedin_url !== undefined && { linkedin_url: variables.linkedin_url }),
      }));
      return { previousDisplay };
    },
    onSuccess: () => {
      setIsEditing(false);
      setIsEditingInfo(false);
      setDuplicateData(null);
      onUpdate?.();
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
    onError: (err, _variables, context) => {
      // Roll back the optimistic display patch — the save didn't land.
      if (context?.previousDisplay) setDisplayInfo(context.previousDisplay);
      if (err.isDuplicate) {
        setDuplicateData({
          duplicates: err.duplicates,
          targetDeptId: editForm.department_id,
        });
      } else {
        failWith('save this contact')(err);
      }
    },
  });

  // Merge contacts mutation
  const mergeMutation = useMutation({
    mutationFn: ({ targetId, sourceId }) =>
      apiClient.post(`/api/workspace/contacts/${targetId}/merge`, { source_id: sourceId }),
    onSuccess: () => {
      setDuplicateData(null);
      setIsEditing(false);
      onUpdate?.();
      onClose?.();
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
    onError: failWith('merge these contacts'),
  });

  // Add note mutation — optimistic: prepend a temp note to the cache
  // immediately, roll back on error, reconcile on settle.
  const addNoteMutation = useMutation({
    mutationFn: (noteData) =>
      apiClient.post(`/api/workspace/contacts/${contact.id}/notes`, noteData),
    onMutate: async (noteData) => {
      const key = ['contact-notes', contact.id];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      const tempNote = {
        id: `temp-${Date.now()}`,
        content: noteData.content,
        note_type: noteData.note_type,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData(key, (prev) => ({
        ...prev,
        notes: [tempNote, ...(prev?.notes || [])],
      }));
      setNewNote('');
      return { previous };
    },
    onError: (_err, noteData, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['contact-notes', contact.id], context.previous);
      }
      // Restore the draft so the user's text isn't lost.
      setNewNote(noteData.content);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-notes', contact.id] });
    },
  });

  // Delete note mutation — optimistic: drop the note from the cache
  // immediately, roll back on error, reconcile on settle.
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId) =>
      apiClient.del(`/api/workspace/contacts/${contact.id}/notes/${noteId}`),
    onMutate: async (noteId) => {
      const key = ['contact-notes', contact.id];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (prev) => ({
        ...prev,
        notes: (prev?.notes || []).filter((n) => n.id !== noteId),
      }));
      return { previous };
    },
    onError: (err, _noteId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['contact-notes', contact.id], context.previous);
      }
      failWith('delete the note')(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-notes', contact.id] });
    },
  });

  // Delete contact mutation
  const deleteContactMutation = useMutation({
    mutationFn: () => apiClient.del(`/api/workspace/contacts/${contact.id}`),
    onSuccess: () => {
      onUpdate?.();
      onClose?.();
      queryClient.invalidateQueries({ queryKey: ['workspace'] });
    },
    onError: failWith('delete this contact'),
  });

  const handleSaveEdit = () => {
    setMutationError(null);
    updateMutation.mutate(editForm);
  };

  const handleSaveInfo = () => {
    updateMutation.mutate({
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      title: editForm.title,
      email: editForm.email,
      phone: editForm.phone,
      linkedin_url: editForm.linkedin_url,
    });
  };

  // Handle merge with selected duplicate
  const handleMerge = async (duplicateId) => {
    setIsMerging(true);
    try {
      // Merge current contact into the duplicate (duplicate becomes the target, keeps its position)
      await mergeMutation.mutateAsync({ targetId: duplicateId, sourceId: contact.id });
    } finally {
      setIsMerging(false);
    }
  };

  // Handle "Keep Both" - proceed with transfer, skip duplicate check
  const handleProceedAnyway = () => {
    setDuplicateData(null);
    updateMutation.mutate({ ...editForm, skip_duplicate_check: true });
  };

  // Handle cancel duplicate resolution
  const handleCancelDuplicate = () => {
    setDuplicateData(null);
  };

  const handleAddNote = (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    addNoteMutation.mutate({
      content: newNote.trim(),
      note_type: noteType,
    });
  };

  const currentRel = REL_OPTIONS.find((r) => r.value === contact.relationship_status);

  return (
    <OverlayPortal>
    <div className="cdm-overlay" {...modal.overlayProps}>
      <div className="cdm-modal" {...modal.dialogProps} aria-label="Contact details" onClick={(e) => e.stopPropagation()}>
        <div className="cdm-header">
          <div className="cdm-header-info">
            <div className="cdm-avatar">
              <User size={24} />
            </div>
            <div>
              <h2 className="cdm-name">{fullName}</h2>
              {displayInfo.title && <p className="cdm-title">{displayInfo.title}</p>}
            </div>
          </div>
          <div className="cdm-header-actions">
            <button
              className="cdm-delete-btn"
              onClick={() => setShowDeleteConfirm(true)}
              aria-label="Delete contact"
              title="Delete contact"
            >
              <Trash2 size={16} />
            </button>
            <button className="cdm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Unsaved-edit guard */}
        {modal.confirming && (
          <div className="cdm-delete-confirm" role="alert">
            <AlertTriangle size={16} />
            <span>Discard unsaved edits?</span>
            <div className="cdm-delete-confirm-actions">
              <button className="cdm-btn-cancel" onClick={() => modal.setConfirming(false)}>Keep editing</button>
              <button className="cdm-btn-delete" onClick={onClose}>Discard</button>
            </div>
          </div>
        )}

        {/* Mutation failure — never silent */}
        {mutationError && (
          <div className="cdm-delete-confirm" role="alert" style={{ background: 'rgba(var(--color-error-rgb), 0.08)' }}>
            <AlertCircle size={16} />
            <span>
              Couldn't {mutationError.action}. {mutationError.detail}
            </span>
            <div className="cdm-delete-confirm-actions">
              <button className="cdm-btn-cancel" onClick={() => setMutationError(null)}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="cdm-delete-confirm">
            <AlertTriangle size={16} />
            {/* CRM-14: delete is soft + re-parents reports; the old copy said
                "cannot be undone", which is both wrong and under-disclosing. */}
            <span>Delete {fullName}? Their direct reports move up to {fullName}'s manager. This is a soft delete — you can restore them from the trash.</span>
            <div className="cdm-delete-confirm-actions">
              <button
                className="cdm-btn-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteContactMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="cdm-btn-delete"
                onClick={() => deleteContactMutation.mutate()}
                disabled={deleteContactMutation.isPending}
              >
                {deleteContactMutation.isPending ? (
                  <Loader2 size={14} className="cdm-spinner" />
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div className="cdm-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`cdm-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {Icon && <Icon size={14} />}
                {tab.label}
                {tab.id === 'meetings' && recordings.length > 0 && (
                  <span className="cdm-tab-count">{recordings.length}</span>
                )}
                {tab.id === 'news' && newsTotal > 0 && (
                  <span className="cdm-tab-count">{newsTotal}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="cdm-body">
          {/* Contact Info Tab */}
          {activeTab === 'info' && (
          <div className="cdm-section">
            <div className="cdm-section-header">
              <h3 className="cdm-section-title">Contact Info</h3>
              {!isEditingInfo && (
                <button className="cdm-edit-btn" onClick={() => setIsEditingInfo(true)}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>

            {isEditingInfo ? (
              <div className="cdm-edit-form">
                <div className="cdm-edit-row">
                  <label>
                    <User size={12} /> First Name
                  </label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>
                    <User size={12} /> Last Name
                  </label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last name"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>
                    <Briefcase size={12} /> Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="VP of Engineering"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>
                    <Mail size={12} /> Email
                  </label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>
                    <Phone size={12} /> Phone
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>
                    <Linkedin size={12} /> LinkedIn
                  </label>
                  <input
                    type="url"
                    value={editForm.linkedin_url}
                    onChange={(e) => setEditForm((f) => ({ ...f, linkedin_url: e.target.value }))}
                    placeholder="linkedin.com/in/username"
                  />
                </div>
                <div className="cdm-edit-actions">
                  <button
                    className="cdm-btn-cancel"
                    onClick={() => setIsEditingInfo(false)}
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    className="cdm-btn-save"
                    onClick={handleSaveInfo}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <Loader2 size={14} className="cdm-spinner" />
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="cdm-info-grid">
                {(contact.department_id || contact.account_name) && (
                  <div className="cdm-info-item">
                    <Building2 size={14} />
                    <span>
                      {departments.find((d) => d.department_id === contact.department_id)?.official_name ||
                       departments.find((d) => d.department_id === contact.department_id)?.common_name ||
                       contact.account_name || 'Unknown organization'}
                    </span>
                  </div>
                )}
                <div className="cdm-info-item">
                  <Briefcase size={14} />
                  {displayInfo.title ? (
                    <span>{displayInfo.title}</span>
                  ) : (
                    <span className="cdm-empty-field" onClick={() => setIsEditingInfo(true)}>Add title</span>
                  )}
                </div>
                <div className="cdm-info-item">
                  <Mail size={14} />
                  {displayInfo.email ? (
                    <a href={`mailto:${displayInfo.email}`}>{displayInfo.email}</a>
                  ) : (
                    <span className="cdm-empty-field" onClick={() => setIsEditingInfo(true)}>Add email</span>
                  )}
                </div>
                <div className="cdm-info-item">
                  <Phone size={14} />
                  {displayInfo.phone ? (
                    <a href={`tel:${displayInfo.phone}`}>{displayInfo.phone}</a>
                  ) : (
                    <span className="cdm-empty-field" onClick={() => setIsEditingInfo(true)}>Add phone</span>
                  )}
                </div>
                <div className="cdm-info-item">
                  <Linkedin size={14} />
                  {displayInfo.linkedin_url ? (
                    <a href={displayInfo.linkedin_url.startsWith('http') ? displayInfo.linkedin_url : `https://${displayInfo.linkedin_url}`} target="_blank" rel="noopener noreferrer">
                      {displayInfo.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  ) : (
                    <>
                      <span className="cdm-empty-field" onClick={() => setIsEditingInfo(true)}>Add LinkedIn</span>
                      <a
                        href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(
                          `${contact.first_name} ${contact.last_name || ''} ${contact.account_name || ''}`.trim()
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)' }}
                      >
                        Search LinkedIn ↗
                      </a>
                    </>
                  )}
                </div>
                {(contact.reports_to_id || contact.reports_to) && (
                  <div className="cdm-info-item">
                    <Briefcase size={14} />
                    <span>
                      Reports to:{' '}
                      {contact.reports_to_id
                        ? managerOptions.find((m) => m.value === contact.reports_to_id)?.label || contact.reports_to || 'Unknown'
                        : contact.reports_to}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Status Tab */}
          {activeTab === 'status' && (
          <div className="cdm-section">
            <div className="cdm-section-header">
              <h3 className="cdm-section-title">Status</h3>
              {!isEditing && (
                <button className="cdm-edit-btn" onClick={() => setIsEditing(true)}>
                  <Pencil size={12} /> Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="cdm-edit-form">
                <div className="cdm-edit-row">
                  <label>
                    <Building2 size={12} /> Organization
                  </label>
                  <DepartmentSearchDropdown
                    departments={departments}
                    value={editForm.department_id}
                    onChange={(deptId) => setEditForm((f) => ({ ...f, department_id: deptId }))}
                    placeholder="Search agencies..."
                  />
                </div>
                {isDepartmentChanging && editForm.reports_to_id && (
                  <div className="cdm-account-warning">
                    <AlertTriangle size={12} />
                    <span>Changing organization will clear "Reports To" since org charts don't transfer.</span>
                  </div>
                )}
                <div className="cdm-edit-row">
                  <label>Relationship</label>
                  <select
                    value={editForm.relationship_status}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, relationship_status: e.target.value }))
                    }
                  >
                    <option value="">Select...</option>
                    {REL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cdm-edit-row">
                  <label>Influence</label>
                  <select
                    value={editForm.influence_level}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, influence_level: e.target.value }))
                    }
                  >
                    <option value="">Select...</option>
                    {INFLUENCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cdm-edit-row">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="VP of Engineering"
                  />
                </div>
                <div className="cdm-edit-row">
                  <label>Reports To</label>
                  <select
                    value={editForm.reports_to_id}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, reports_to_id: e.target.value }))
                    }
                  >
                    <option value="">— No manager (top level) —</option>
                    {managerOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="cdm-edit-actions">
                  <button
                    className="cdm-btn-cancel"
                    onClick={() => setIsEditing(false)}
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    className="cdm-btn-save"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <Loader2 size={14} className="cdm-spinner" />
                    ) : (
                      'Save'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="cdm-status-display">
                {currentRel && (
                  <span
                    className="cdm-status-badge"
                    style={{ borderColor: currentRel.color, color: currentRel.color }}
                  >
                    {currentRel.label}
                  </span>
                )}
                {contact.influence_level && contact.influence_level !== 'unknown' && (
                  <span className="cdm-influence-badge">
                    {INFLUENCE_OPTIONS.find((i) => i.value === contact.influence_level)?.label}
                  </span>
                )}
                {!currentRel && !contact.influence_level && (
                  <span className="cdm-no-status">No status set</span>
                )}
              </div>
            )}
          </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
          <div className="cdm-section cdm-notes-section">
            <h3 className="cdm-section-title">
              <MessageSquare size={14} /> Notes & Talking Points
            </h3>

            {notesLoading ? (
              <div className="cdm-notes-loading">
                <Loader2 size={16} className="cdm-spinner" /> Loading notes...
              </div>
            ) : notes.length === 0 ? (
              <p className="cdm-notes-empty">
                No notes yet. Add notes or talking points to prepare for meetings.
              </p>
            ) : (
              <div className="cdm-notes-list">
                {notes.map((note) => {
                  const TypeIcon = NOTE_TYPES.find((t) => t.value === note.note_type)?.icon || StickyNote;
                  return (
                    <div key={note.id} className={`cdm-note cdm-note-${note.note_type}`}>
                      <div className="cdm-note-header">
                        <span className="cdm-note-type">
                          <TypeIcon size={12} />
                          {NOTE_TYPES.find((t) => t.value === note.note_type)?.label || 'Note'}
                        </span>
                        <span className="cdm-note-date">{formatDate(note.created_at)}</span>
                        <button
                          className="cdm-note-delete"
                          onClick={() => {
                            // X-7: note delete is a hard delete — arm on first
                            // click, delete on the second (3s auto-disarm).
                            if (armedNoteId === note.id) {
                              setArmedNoteId(null);
                              deleteNoteMutation.mutate(note.id);
                            } else {
                              setArmedNoteId(note.id);
                              setTimeout(() => setArmedNoteId((cur) => (cur === note.id ? null : cur)), 3000);
                            }
                          }}
                          disabled={deleteNoteMutation.isPending}
                          aria-label={armedNoteId === note.id ? 'Confirm delete note' : 'Delete note'}
                          title={armedNoteId === note.id ? 'Click again to delete' : 'Delete note'}
                        >
                          {armedNoteId === note.id
                            ? <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-error, #ef4444)' }}>Sure?</span>
                            : <Trash2 size={12} />}
                        </button>
                      </div>
                      <p className="cdm-note-content">{note.content}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Note Form */}
            <form className="cdm-add-note" onSubmit={handleAddNote}>
              <div className="cdm-note-type-select">
                {NOTE_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={`cdm-note-type-btn ${noteType === type.value ? 'active' : ''}`}
                      onClick={() => setNoteType(type.value)}
                    >
                      <Icon size={12} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
              <div className="cdm-note-input-row">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder={
                    noteType === 'talking_point'
                      ? 'Add a talking point for your next meeting...'
                      : 'Add a note about this contact...'
                  }
                  rows={2}
                />
                <button
                  type="submit"
                  className="cdm-add-note-btn"
                  disabled={!newNote.trim() || addNoteMutation.isPending}
                >
                  {addNoteMutation.isPending ? (
                    <Loader2 size={14} className="cdm-spinner" />
                  ) : (
                    <Plus size={14} />
                  )}
                </button>
              </div>
              {addNoteMutation.isError && (
                <div className="cdm-note-error">
                  <AlertCircle size={12} /> Failed to add note
                </div>
              )}
            </form>
          </div>
          )}

          {/* Meetings Tab */}
          {activeTab === 'meetings' && (
          <div className="cdm-section cdm-meetings-section">
            <h3 className="cdm-section-title">
              <Volume2 size={14} /> Meeting Recordings
            </h3>

            {recordingsLoading ? (
              <div className="cdm-notes-loading">
                <Loader2 size={16} className="cdm-spinner" /> Loading recordings...
              </div>
            ) : recordings.length === 0 ? (
              <div className="cdm-meetings-empty">
                <Volume2 size={24} strokeWidth={1.5} />
                <p>No recordings linked to this contact yet.</p>
                <p className="cdm-meetings-hint">
                  Link recordings from the transcript viewer.
                </p>
              </div>
            ) : (
              <div className="cdm-recordings-list">
                {recordings.map((recording) => (
                  <PlaudRecordingCard
                    key={recording.id}
                    recording={recording}
                    onViewTranscript={setViewingTranscript}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
          )}

          {/* News Tab */}
          {activeTab === 'news' && (
          <div className="cdm-section cdm-news-section">
            <h3 className="cdm-section-title">
              <Newspaper size={14} /> In the news
              {newsTotal > 0 && (
                <a
                  className="cdm-news-all"
                  href={`/news?scope=all&q=${encodeURIComponent(`"${fullName}"`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Search the full feed for this name"
                >
                  Full feed <ExternalLink size={11} />
                </a>
              )}
            </h3>

            {newsLoading ? (
              <div className="cdm-notes-loading">
                <Loader2 size={16} className="cdm-spinner" /> Loading news...
              </div>
            ) : newsError ? (
              <p className="nf-notice nf-notice--err">{newsError.message || 'Could not load news.'}</p>
            ) : newsItems.length === 0 ? (
              <div className="cdm-meetings-empty">
                <Newspaper size={24} strokeWidth={1.5} />
                <p>No stories mention this contact yet.</p>
                <p className="cdm-meetings-hint">
                  Stories are matched to people by full name as the feeds come in; a TekStak signal
                  (title change, move) lands here too.
                </p>
              </div>
            ) : (
              <div className="nf-strip-list">
                {newsItems.map((it) => (
                  <NewsItemRow key={it.id} item={it} compact onDepartment={() => {}} onContact={() => {}} />
                ))}
                {newsData?.next_before && (
                  <p className="cdm-meetings-hint cdm-news-more">Showing the latest {newsItems.length} of {newsTotal}.</p>
                )}
              </div>
            )}
          </div>
          )}

          {activeTab === 'sources' && (
          <div className="cdm-section">
            <h3 className="cdm-section-title"><History size={14} /> Where this came from</h3>
            <p className="cdm-sources-lead">
              Every field carries who put it there. An assistant’s claim fills blanks and can be
              replaced by a better one; what you confirm yourself is never overwritten.
            </p>

            <div className="cdm-sources-fields">
              {PROV_FIELDS.map((field) => {
                const value = field === 'reports_to' ? contact.reports_to_id : contact[field];
                const entry = provenance[field];
                if (!value && !entry) return null;
                const shown = field === 'reports_to'
                  ? (contact.reports_to || allContacts.find((c) => c.id === contact.reports_to_id)?.first_name || 'set')
                  : value;
                return (
                  <div className="cdm-sources-row" key={field}>
                    <span className="cdm-sources-field">{fieldLabel(field)}</span>
                    <span className="cdm-sources-value">{shown || <em>empty</em>}</span>
                    {entry
                      ? <ClaimBadge entry={entry} />
                      : <span className="cdm-claim cdm-claim-human" title="Entered before claims were tracked — treated as yours.">Entered by you</span>}
                    {entry?.basis && <span className="cdm-sources-basis">{describeClaim(entry)}</span>}
                  </div>
                );
              })}
              {PROV_FIELDS.every((f) => !(f === 'reports_to' ? contact.reports_to_id : contact[f]) && !provenance[f]) && (
                <p className="cdm-sources-empty">Only a name so far — nothing else has been gathered yet.</p>
              )}
            </div>

            <h4 className="cdm-sources-head">Changes</h4>
            {historyLoading && <p className="cdm-sources-empty"><Loader2 size={14} className="cdm-spinner" /> Loading the trail…</p>}
            {historyError && <p className="cdm-sources-empty">Couldn’t load this contact’s history.</p>}
            {!historyLoading && !historyError && changes.length === 0 && (
              <p className="cdm-sources-empty">No changes logged yet — the trail starts at the first edit after Sept 2026.</p>
            )}
            {changes.length > 0 && (
              <ul className="cdm-sources-log">
                {changes.map((ch) => (
                  <li key={ch.id}>
                    <span className="cdm-sources-when">{formatDate(ch.at)}</span>
                    <span className="cdm-sources-what">
                      {ch.action}
                      {Object.keys(ch.changed || {}).length > 0
                        ? ` · ${Object.keys(ch.changed).map(fieldLabel).join(', ')}`
                        : ''}
                    </span>
                    <span className="cdm-sources-who">
                      {(ch.actor || 'unrecorded').replace(/^key:/, '')}{ch.via ? ` via ${ch.via}` : ''}
                    </span>
                    {ch.basis && <span className="cdm-sources-basis">{ch.basis}</span>}
                  </li>
                ))}
              </ul>
            )}

            <div className="cdm-sources-head-row">
              <h4 className="cdm-sources-head">Where they’ve worked</h4>
              <button
                type="button"
                className="cdm-edit-btn"
                onClick={() => { setPasteOpen((o) => !o); setPreview(null); }}
              >
                <Plus size={12} /> {pasteOpen ? 'Cancel' : 'Add past roles'}
              </button>
            </div>

            {pasteOpen && (
              <div className="cdm-paste">
                <p className="cdm-sources-lead">
                  Paste the Experience section from a profile you can see — title, employer and
                  dates, one role after another. Nothing is saved until you’ve read what it found.
                </p>
                <textarea
                  className="cdm-paste-box"
                  rows={7}
                  value={pasteText}
                  placeholder={'Chief Information Officer\nCalifornia Department of Technology\nApr 2026 - Present\n\nDeputy Director\nOracle\nJan 2015 - Mar 2019'}
                  onChange={(e) => { setPasteText(e.target.value); setPreview(null); }}
                />
                <div className="cdm-paste-actions">
                  <button
                    type="button"
                    className="cdm-edit-btn"
                    disabled={!pasteText.trim() || employmentMutation.isPending}
                    onClick={() => employmentMutation.mutate({ text: pasteText, dryRun: true })}
                  >
                    {employmentMutation.isPending ? <Loader2 size={12} className="cdm-spinner" /> : null} Read it
                  </button>
                  {preview && (preview.added > 0 || preview.updated > 0) && (
                    <button
                      type="button"
                      className="cdm-edit-btn cdm-edit-btn--primary"
                      disabled={employmentMutation.isPending}
                      onClick={() => employmentMutation.mutate({ text: pasteText, dryRun: false })}
                    >
                      Save {preview.added + preview.updated} role{preview.added + preview.updated === 1 ? '' : 's'}
                    </button>
                  )}
                </div>

                {preview && (
                  <div className="cdm-paste-preview">
                    {(preview.plan || []).map((row, i) => (
                      <div className="cdm-sources-row" key={`plan-${i}`}>
                        <span className={`cdm-claim cdm-claim-${row.action === 'kept' ? 'human' : 'automated'}`}>
                          {row.action === 'add' ? 'new' : row.action === 'update' ? 'fills' : 'have it'}
                        </span>
                        <span className="cdm-sources-value">{row.title || 'role unrecorded'}</span>
                        <span className="cdm-sources-who">{row.employer || '—'}</span>
                        {row.startedAt && (
                          <span className="cdm-sources-when">
                            {formatDate(row.startedAt)}{row.endedAt ? ` – ${formatDate(row.endedAt)}` : ''}
                          </span>
                        )}
                        {row.reason && <span className="cdm-sources-basis">{row.reason}</span>}
                      </div>
                    ))}
                    {(preview.refused || []).map((r, i) => (
                      <div className="cdm-sources-row" key={`refused-${i}`}>
                        <span className="cdm-claim cdm-claim-guessed">skipped</span>
                        <span className="cdm-sources-value">{r.entry?.title || r.entry?.employer || 'a role'}</span>
                        <span className="cdm-sources-basis">{r.reason}</span>
                      </div>
                    ))}
                    {(preview.unparsed || []).map((u, i) => (
                      <div className="cdm-sources-row" key={`unparsed-${i}`}>
                        <span className="cdm-claim cdm-claim-guessed">unread</span>
                        <span className="cdm-sources-basis">{u.why}</span>
                      </div>
                    ))}
                    {!preview.plan?.length && !preview.refused?.length && !preview.unparsed?.length && (
                      <p className="cdm-sources-empty">Nothing readable in that paste.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {tenures.length > 0 ? (
              <ul className="cdm-sources-log">
                {tenures.map((t) => (
                  <li key={t.id}>
                    <span className="cdm-sources-when">
                      {formatDate(t.startedAt)}{t.endedAt ? ` – ${formatDate(t.endedAt)}` : ' – now'}
                    </span>
                    <span className="cdm-sources-what">{t.title || 'role unrecorded'}</span>
                    <span className="cdm-sources-who">{t.employer || t.accountName || 'unknown employer'}</span>
                    {t.source && t.source !== 'crm' && (
                      <span className="cdm-claim cdm-claim-automated">{t.source}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cdm-sources-empty">
                Only what the CRM has watched so far. Add earlier roles to see the whole career.
              </p>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Transcript Modal */}
      {viewingTranscript && (
        <PlaudTranscriptModal
          recording={viewingTranscript}
          onClose={() => setViewingTranscript(null)}
          contacts={allContacts}
        />
      )}

      {/* Duplicate Detection Modal */}
      {duplicateData && (
        <DuplicateContactModal
          contact={contact}
          duplicates={duplicateData.duplicates}
          targetDeptName={
            departments.find((d) => d.department_id === duplicateData.targetDeptId)?.official_name ||
            departments.find((d) => d.department_id === duplicateData.targetDeptId)?.common_name ||
            'target agency'
          }
          onMerge={handleMerge}
          onProceed={handleProceedAnyway}
          onCancel={handleCancelDuplicate}
          isMerging={isMerging || mergeMutation.isPending}
        />
      )}
    </div>
    </OverlayPortal>
  );
}

export default ContactDetailModal;
